/**
 * Short Web Audio beep for notification cues. No asset bundling — synthesised
 * on demand. Distinct frequency per category so users can tell moderator
 * approval requests apart from incoming invites without looking at the screen.
 *
 * Browsers gate AudioContext until the first user gesture; calls before then
 * silently no-op (the context stays in 'suspended' state). Once the user has
 * interacted with the page at all, subsequent beeps play normally.
 */

export type NotificationSoundKind = 'moderator' | 'invite' | 'journal'

let audioCtx: AudioContext | null = null

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (audioCtx) return audioCtx
  const Ctor = window.AudioContext || (window as any).webkitAudioContext
  if (!Ctor) return null
  try {
    audioCtx = new Ctor()
  } catch {
    return null
  }
  return audioCtx
}

// Per-kind tone profile. Two short pips for moderator (urgent feel), one
// gentle tone for invite. Frequencies sit in the comfortable mid range so
// they're audible without being shrill on small device speakers.
function toneProfile(kind: NotificationSoundKind): { freq: number; durationMs: number; pulses: number }[] {
  switch (kind) {
    case 'moderator':
      return [
        { freq: 880, durationMs: 110, pulses: 2 },
      ]
    case 'invite':
      return [
        { freq: 520, durationMs: 80, pulses: 1 },
        { freq: 720, durationMs: 140, pulses: 1 },
      ]
    case 'journal':
      // One soft mid pip for a new journal entry — gentler than the moderator
      // double-pip so it reads as "something arrived" rather than "act now".
      return [
        { freq: 660, durationMs: 120, pulses: 1 },
      ]
  }
}

export function playNotificationSound(kind: NotificationSoundKind): void {
  const ctx = getContext()
  if (!ctx) return
  // If the context is suspended (no user gesture yet), try to resume once.
  // Failure is fine — the next interaction will let subsequent calls through.
  if (ctx.state === 'suspended') {
    void ctx.resume().catch(() => {})
  }

  const now = ctx.currentTime
  let cursor = now
  for (const part of toneProfile(kind)) {
    for (let i = 0; i < part.pulses; i++) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(part.freq, cursor)
      // Quick attack + short decay so the beep doesn't click on start/stop.
      gain.gain.setValueAtTime(0.0001, cursor)
      gain.gain.exponentialRampToValueAtTime(0.18, cursor + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, cursor + part.durationMs / 1000)
      osc.connect(gain).connect(ctx.destination)
      osc.start(cursor)
      osc.stop(cursor + part.durationMs / 1000 + 0.02)
      cursor += part.durationMs / 1000 + 0.05
    }
  }
}
